'use client'

import {Button, ButtonSpinner, Form, Input, Locale, Slideout} from '@local/ui/components'

import CloseSVG from '@local/assets/svgs/icon-close.svg'
import {EMAIL_PATTERN, emailIsValid} from '@local/utils/string'
import {useRef, useState, useEffect} from 'react'

export interface ProductSliderSlideoutProps {
	isOpen: boolean
	onClickClose: () => void
	retreat: string
	notifyPanel:{
		title: string
		text: string
		mailchimpId: string
		successMessage:string
	}
}

export const ProductSliderSlideout = ({isOpen, onClickClose, retreat, notifyPanel}: ProductSliderSlideoutProps) => {
	const inputRef = useRef<HTMLInputElement>(null)
	const [email, setEmail] = useState<string>('')
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [isSuccess, setIsSuccess] = useState<boolean>(false)
	const [hasError, setHasError] = useState<boolean>(false)

	const handleSubmit = async ({body}) => {
		setIsLoading(true)
		try {
			setIsSuccess(false)
			setHasError(false)

			if (!emailIsValid(body.email)) {
				throw new Error('InvalidEmail')
			}

			const res = await fetch('/api/coming-soon/subscribe/', {
				method: 'POST',
				body: JSON.stringify({email_address: body.email, listID: notifyPanel.mailchimpId, experience: retreat}),
			})
			const result = await res.json()

			if (res.status === 200) {
				setIsSuccess(true)
				setIsLoading(false)
				return {status: 200}
			} else {
				setIsLoading(false)
				setHasError(true)
				return {errors: [result]}
			}
		} catch (error) {
			setIsLoading(false)
			setHasError(true)

			if (error.message === 'InvalidEmail') {
				return {errors: [{code: 400, message: 'Invalid email'}]}
			}
			if (error.message === 'RequestFailed') {
				return {errors: [{code: 500, message: 'Request failed'}]}
			}
		}
	}

	useEffect(() => {
		if (isSuccess) {
			setTimeout(() => {
				setIsSuccess(false)
			}, 2000)
		}
	}, [isSuccess])

	const handleAdditionalReset = () => {
		inputRef.current?.blur()
		setEmail('')
	}

	return (
		<Slideout
			isOpen={isOpen}
			variant='right'
			onClickClose={onClickClose}
			className='bg-grey-4 text-white'>

			<div className='flex items-center justify-between w-full py-10 lg:py-20'>
				<h3 className='w-fit uppercase'>{notifyPanel.title}</h3>
				<Button
					small
					variant='icon'
					onClick={onClickClose}
					className='px-16 aspect-square'>
					<CloseSVG />
				</Button>
			</div>

			<Form
				autoReset
				additionalReset={handleAdditionalReset}
				onSubmit={handleSubmit}
				successMessage={<SuccessPanel title={notifyPanel.successMessage} text={notifyPanel.text} />}
				errorClassName='top-full'
				successClassName='z-5 mt-0 -inset-1 w-[calc(100%+2px)]'
				className='realtive flex flex-col w-full pt-40 gap-14'>
				<p className='text-12 text-grey-1 lg:span-mr-1-wide'>{notifyPanel.text}</p>
				<Input
					variant='dark'
					type='email'
					name='email'
					label='Email'
					required
					id='product-slider-email'
					ref={inputRef}
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					onInvalid={() => setHasError(true)}
					pattern={EMAIL_PATTERN}
					hasError={hasError}
					className='h-48'
					inputClassName='h-full'
					errorClassName='z-10 bg-grey-4'
					labelClassName='bg-grey-4' />
				<ButtonSpinner
					loading={isLoading}
					variant='primary-transparent'>
					<Locale>Send</Locale>
				</ButtonSpinner>
			</Form>
		</Slideout>
	)
}

const SuccessPanel = ({title, text}) => (
	<div className='flex flex-col items-start pt-40 w-full h-full gap-14 bg-grey-4 normal-case text-left'>
		<h3 className='text-32 font-serif tracking-tight'>{title}</h3>
		<p className='text-12 text-grey-1 lg:span-mr-1-wide'>{text}</p>
	</div>
)
